import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { isPolicyIssuedToUser } from 'features/policy'
import { createRef, createRsrcKey, isResourceManaged } from 'features/resources'
import _ from 'lodash'
import { useCallback } from 'react'
import { TargetsSliceNames, getTargetsPolicyRules } from '../utils'
import { useUser } from 'Core/Hooks/useUser'

export const useTargetAccess = () => {
  const { slices } = useMultiSlice(['policyList', ...TargetsSliceNames, 'groupList'])
  const { user } = useUser()
  /**
   * Returns all the targets which has policy created. Regardless of to whom the target is granted
   */
  const getAccessGrantedTargetsFromPolicy = useCallback(() => {
    const mapObj = {}
    slices.policyList.forEach((policy) => {
      if (!isPolicyIssuedToUser(policy, user)) return
      const rules = _.flatten(Object.values(getTargetsPolicyRules(policy)))
      rules.forEach((rule) => {
        const key = `${rule.ObjectRef.RefKind}+${rule.ObjectRef.RefID}`
        // Don't add any iam actions - iam actions carry iam rsrcs
        if (rule.ObjectRef.RefKind === 'IamAction') return
        if (!mapObj[key]) mapObj[key] = true
      })
    })
    return mapObj
  }, [slices])

  const getAllTargetsMapObj = () => {
    const mapObj = {}
    TargetsSliceNames.forEach((sliceName) => {
      slices[sliceName].forEach((e) => {
        // Only managed resource are given access
        if (isResourceManaged(e)) mapObj[createRsrcKey(e)] = []
      })
    })
    return mapObj
  }

  /**
   * Get all the targets which the `user` has access to.
   *
   * Sample:
   * ```
   *    {
   *      'Server+1234': true
   *      'AppRole+6670': true
   *    }
   * ```
   */
  const getUserAccessTargetsMap = (user) => {
    const mapObj = {}

    slices.policyList.forEach((policy) => {
      // Return if the policy is not issued to this user or if this is a iam resources policy
      if (!isPolicyIssuedToUser(policy, user) || policy.Type === 'IAMAction') return

      const accessTypes = Object.keys(policy.Spec.ActionMap || {})
      const allRules = accessTypes.flatMap((type) => policy.Spec.ActionMap[type]?.PolicyRule || [])

      allRules.forEach((rule) => {
        const key = `${rule.ObjectRef.RefKind}+${rule.ObjectRef.RefID}`
        // Don't add any iam actions - iam actions carry iam rsrcs and application type
        if (rule.ObjectRef.RefKind === 'IamAction' || rule.ObjectRef.RefKind === 'Application')
          return
        const policyRefs = mapObj[key] || []
        policyRefs.push(createRef(policy))
        mapObj[key] = policyRefs
      })
    })

    return mapObj
  }

  return { getAccessGrantedTargetsFromPolicy, getUserAccessTargetsMap }
}
